<template>
	<div id="myMeetings">
		<page-header title="我的会议" :count="total" unit="场">
			<el-select v-model="filterMode" placeholder="会议类型" clearable size="small" @change="onFilterChange">
				<el-option label="全部类型" value="" />
				<el-option label="线上会" value="TENCENT" />
				<el-option label="线下会" value="OFFLINE" />
				<el-option label="仅管理" value="OTHER" />
			</el-select>
			<el-input v-model="filterKeyword" class="ph-search" placeholder="搜索会议名称" clearable size="small"
				prefix-icon="el-icon-search"
				@clear="onFilterChange" @keyup.enter.native="onFilterChange" />
			<el-button type="primary" size="small" icon="el-icon-search" @click="onFilterChange">搜索</el-button>
			<el-button v-if="filterMode || filterKeyword" size="small" icon="el-icon-refresh"
				@click="resetFilter">重置</el-button>
		</page-header>

		<!-- 会议列表 -->
		<div class="meeting-list" v-loading="loading">
			<div class="meeting-card" v-for="item in meetingList" :key="item.id"
				:class="{ 'is-completed': !isLiving(item) && !isUpcoming(item) }">

				<!-- 左侧：时间区块 -->
				<div class="time-section">
					<div class="month">{{ formatMonth(item.meetingStartTime) }}</div>
					<div class="day">{{ formatDay(item.meetingStartTime) }}</div>
					<div class="time">{{ formatTime(item.meetingStartTime) }}</div>
				</div>

				<!-- 分隔线 -->
				<div class="divider"></div>

				<!-- 中间：会议信息 -->
				<div class="info-section">
					<h3 class="meeting-title">
						{{ item.meetingTitle }}
						<source-badge :source="item.source" />
					</h3>
					<div class="meeting-meta">
						<span class="mode-tag" :class="getModeClass(item)">
							<i :class="getModeIcon(item)"></i>{{ getModeText(item) }}
						</span>
						<span v-if="item.projectName">
							<i class="el-icon-folder"></i>{{ item.projectName }}
						</span>
						<span v-if="item.meetingAddress">
							<i class="el-icon-location-outline"></i>{{ item.meetingAddress }}
						</span>
						<span v-if="item.meetingEndTime">
							<i class="el-icon-time"></i>{{ formatTime(item.meetingStartTime) }} - {{ formatTime(item.meetingEndTime) }}
						</span>
						<span v-if="item.meetingDays">
							<i class="el-icon-date"></i>会期 {{ item.meetingDays }} 天
						</span>
					</div>
				</div>

				<!-- 右侧：操作 + 状态 + 详情 -->
				<div class="action-section">
					<!-- 操作按钮组 -->
					<div class="btn-group">
						<!-- 签署按钮（医生角色） -->
						<button class="btn btn-sign" v-if="isDoctor && item.isSign === 'NO'"
							@click="goSign(item)">
							<i class="el-icon-edit-outline"></i>去签署
						</button>
						<button class="btn btn-signed" v-if="isDoctor && item.isSign === 'YES'" disabled>
							<i class="el-icon-check"></i>已签署
						</button>
						<!-- ① 进入会议室（线上会且未结束时显示） -->
						<button class="btn btn-meeting"
							v-if="(isLiving(item) || isUpcoming(item)) && item.meetingMode === 'TRTC_ONLINE'"
							@click="enterMeeting(item)">
							<i class="el-icon-video-camera"></i>进入会议室
						</button>
						<!-- G11: 发起结算(会议已结束且未关联结算单) -->
						<button class="btn btn-settle"
							v-if="canStartSettlement(item)"
							@click="startSettlement(item)">
							<i class="el-icon-money"></i>发起结算
						</button>
						<!-- G11: 结算进度标签(已发起结算时显示) -->
						<button class="btn btn-settle-progress"
							v-if="item.hasSpendingOrder && !isDoctor"
							:class="settleProgressClass(item.settlementStatus)"
							@click="goSpendingDetail(item)">
							<i class="el-icon-s-finance"></i>{{ settleProgressLabel(item.settlementStatus) }}
						</button>
					</div>

					<!-- ② 会议状态标签 -->
					<div class="status-tag" :class="getStatusClass(item)">
						<span class="status-dot" v-if="isLiving(item)"></span>
						{{ getStatusText(item) }}
					</div>
					<!-- 直播中标识 -->
					<div class="live-badge" v-if="item.liveStatus === 1 && item.meetingMode === 'TRTC_ONLINE'">
						<span class="live-dot-anim"></span>直播中
					</div>

					<!-- ③ 会议详情 -->
					<button class="btn btn-detail" @click="goMeetDetail(item)">
						<i class="el-icon-document"></i>会议详情
					</button>
				</div>
			</div>

			<!-- G11: 内嵌新建结算单对话框(步骤条模式) -->
			<spending-order-dialog
				:visible.sync="settleDialogVisible"
				:default-type="'SUPPLIER'"
				:stepper="true"
				:org-options="orgOptions"
				:supplier-options="supplierOptions"
				:collect-way-options="collectWayOptions"
				:prefill-meeting="prefillMeeting"
				@created="onSettlementCreated" />

			<!-- 空状态 -->
			<div class="empty-state" v-if="!loading && !meetingList.length">
				<div class="empty-icon">
					<i class="el-icon-date"></i>
				</div>
				<p>暂无会议</p>
				<p class="empty-hint">{{ filterMode || filterKeyword ? '未找到匹配的会议，请调整筛选条件' : '您参与的会议将会显示在这里' }}</p>
			</div>
		</div>

		<!-- 分页 -->
		<div class="app-pagination" v-if="total > pageSize">
			<el-pagination
				background
				layout="prev, pager, next, total"
				:total="total"
				:page-size="pageSize"
				:current-page.sync="page"
				@current-change="loadMeetings" />
		</div>
	</div>
</template>

<script>
import { doctorMeetList, getMeetingDetail } from '@/request/api.js';
import { getOrgListCached, getSupplierListCached, getCollectWayCached } from '@/utils/optionsCache.js';
import SpendingOrderDialog from '@/views/type_assistant/z_spending/components/SpendingOrderDialog.vue';
import SourceBadge from '@/components/SourceBadge.vue';
import PageHeader from '@/components/PageHeader.vue';

export default {
	name: 'MyMeetings',
	components: { SpendingOrderDialog, SourceBadge, PageHeader },
	data() {
		return {
			loading: false,
			meetingList: [],
			token: null,
			filterMode: '',   // 会议类型筛选：'', 'TENCENT', 'OFFLINE', 'OTHER'
			filterKeyword: '', // 会议名称搜索关键词
			// 服务端分页
			page: 1,
			pageSize: 10,
			total: 0,
			// G11: 内嵌新建结算单对话框相关
			settleDialogVisible: false,
			prefillMeeting: null,
			orgOptions: [],
			supplierOptions: [],
			collectWayOptions: [],
		}
	},
	computed: {
		isDoctor() {
			const t = this.token?.type;
			return t === 'DOCTOR' || t === 'Doctor';
		},
		role() {
			return this.token?.type;
		}
	},
	created() {
		this.token = JSON.parse(localStorage.getItem('token'));
		// 从详情页返回时携带页码, 恢复到原来停留的那一页
		const fromPage = parseInt(this.$route.query.page, 10);
		if (fromPage > 0) this.page = fromPage;
		this.loadMeetings();
		// G11: 提前预加载结算下拉数据(机构/供应商/收款方式)
		// 这样用户点"发起结算"时, 下拉已经就绪, 不再有"几秒空白"
		// 医生角色不会用到, 跳过节省请求
		if (!this.isDoctor) {
			this.preloadSettleOptions();
		}
	},
	methods: {
		loadMeetings() {
			this.loading = true;
			const params = { current: this.page, size: this.pageSize };
			if (this.filterMode) params.mode = this.filterMode;
			if (this.filterKeyword && this.filterKeyword.trim()) {
				params.keyword = this.filterKeyword.trim();
			}

			doctorMeetList(params).then(res => {
				if (res && res.code === 200) {
					// 用 Object.freeze 避免 Vue 给每条记录加 reactive 监听 (列表只读, 不需要响应式)
					const records = res.data?.records || [];
					records.forEach(Object.freeze);
					this.meetingList = records;
					this.total = res.data?.total || 0;
				} else {
					this.meetingList = [];
					this.total = 0;
					if (res && res.msg && (res.msg.includes('token') || res.msg.includes('登录'))) {
						this.$message.warning('登录已过期，请重新登录');
						setTimeout(() => { this.$router.push({ name: 'login' }); }, 1500);
					} else if (res && res.msg) {
						console.warn('[我的会议] 接口异常:', res.msg);
					}
				}
				this.loading = false;
			}).catch((err) => {
				this.meetingList = [];
				this.total = 0;
				this.loading = false;
				console.error('[我的会议] 请求失败:', err);
			});
		},

		/** 筛选/搜索时回到第 1 页 */
		onFilterChange() {
			this.page = 1;
			this.loadMeetings();
		},

		/** 重置筛选条件 */
		resetFilter() {
			this.filterMode = '';
			this.filterKeyword = '';
			this.page = 1;
			this.loadMeetings();
		},

		/** 获取会议类型显示文字 */
		getModeText(item) {
			switch (item.meetingMode) {
				case 'TRTC_ONLINE': return '线上会';
				case 'OFFLINE': return '线下会';
				case 'EXTERNAL_ONLINE': return '仅管理';
				default: return '其他';
			}
		},

		/** 获取会议类型图标 */
		getModeIcon(item) {
			switch (item.meetingMode) {
				case 'TRTC_ONLINE': return 'el-icon-monitor';
				case 'OFFLINE': return 'el-icon-place';
				case 'EXTERNAL_ONLINE': return 'el-icon-setting';
				default: return 'el-icon-more';
			}
		},

		/** 获取会议类型样式类名 */
		getModeClass(item) {
			switch (item.meetingMode) {
				case 'TRTC_ONLINE': return 'mode-online';
				case 'OFFLINE': return 'mode-offline';
				case 'EXTERNAL_ONLINE': return 'mode-other';
				default: return 'mode-default';
			}
		},

		// G11: 结算进度展示
		settleProgressLabel(status) {
			const map = {
				DRAFT: '草稿',
				COLLECTING_MATERIALS: '收集材料中',
				AI_AUDITING: 'AI 审核中',
				MANUAL_AUDITING: '人工审核中',
				APPROVED: '已通过',
				REJECTED: '已驳回',
				SETTLED: '已结算',
				// 兼容旧值
				SUBMITTED: '已提交',
				OFFICIAL_APPROVED: '官员通过',
				K3_PUSHED: '已推金蝶'
			};
			return '结算: ' + (map[status] || status || '处理中');
		},
		settleProgressClass(status) {
			if (['SETTLED', 'APPROVED', 'K3_PUSHED'].includes(status)) return 'progress-done';
			if (status === 'REJECTED') return 'progress-rejected';
			if (['AI_AUDITING', 'MANUAL_AUDITING', 'SUBMITTED', 'OFFICIAL_APPROVED'].includes(status)) return 'progress-auditing';
			return 'progress-collecting';
		},
		goSpendingDetail(item) {
			// 跳到结算单管理, 后续可加 query 参数自动打开该订单详情
			this.$router.push({
				path: '/spendingManage',
				query: item.settlementOrderId ? { orderId: item.settlementOrderId } : {}
			});
		},

		// G11: 是否可发起结算(会议已结束且未关联结算单)
		canStartSettlement(item) {
			if (this.isDoctor) return false;          // 医生不发起结算
			if (item.hasSpendingOrder) return false;  // 已有结算单
			// 已结束的会议状态(覆盖各种"已结束"语义)
			const endedStatuses = ['FINISH', 'UPLOAD', 'AI_REVIEW', 'AI_REVIEW_SUCCESS',
				'AI_REVIEW_ERROR_ACCEPT', 'OFFICIAL_SUCCESS', 'OFFICIAL_ERROR', 'SUCCESS', 'ERROR'];
			if (endedStatuses.includes(item.meetingStatus)) return true;
			// 时间兜底: 结束时间已过且不在进行中
			if (item.meetingEndTime && !this.isLiving(item)) {
				return new Date(item.meetingEndTime).getTime() < Date.now();
			}
			return false;
		},
		async startSettlement(item) {
			// 首次打开时, 异步加载下拉数据(不阻塞 UI)
			this.preloadSettleOptions();
			// 注入预填会议信息: 机构/项目/会议 均由会议唯一确定, dialog 将锁定不可改
			this.prefillMeeting = {
				id: item.id,
				orgId: item.orgId,
				orgName: item.orgName,         // 后端 listMyMeetings 暂未返回此字段时,dialog 回退展示 orgId
				projectId: item.projectId,
				projectName: item.projectName,
				meetingName: item.meetingTitle || item.meetingName
			};
			this.settleDialogVisible = true;
		},

		// 加载结算下拉数据(走 localStorage 缓存, 4 小时 TTL)
		// 命中缓存时直接同步返回, 几乎零延迟; 没命中才发请求并缓存
		preloadSettleOptions() {
			const freeze = list => {
				if (!Array.isArray(list)) return [];
				list.forEach(Object.freeze);
				return Object.freeze(list);
			};
			if (this.orgOptions.length === 0) {
				getOrgListCached().then(d => { if (d) this.orgOptions = freeze(d); });
			}
			if (this.supplierOptions.length === 0) {
				getSupplierListCached().then(d => { if (d) this.supplierOptions = freeze(d); });
			}
			if (this.collectWayOptions.length === 0) {
				getCollectWayCached().then(d => { if (d) this.collectWayOptions = freeze(d); });
			}
		},

		// 创建成功回调: 问用户是否查看, 选 No 则刷新列表(让按钮消失)
		onSettlementCreated(order) {
			// 标记该会议已有结算单 + 注入进度信息(立即反馈, 不等下次刷新)
			if (this.prefillMeeting?.id) {
				const m = this.meetingList.find(x => x.id === this.prefillMeeting.id);
				if (m) {
					this.$set(m, 'hasSpendingOrder', true);
					this.$set(m, 'settlementOrderId', order.id);
					this.$set(m, 'settlementOrderNo', order.orderNo);
					this.$set(m, 'settlementStatus', order.status || 'DRAFT');
					this.$set(m, 'settlementCurrentStage', order.currentStage);
					this.$set(m, 'settlementType', order.settlementType);
				}
			}
			this.settleDialogVisible = false;
			this.$confirm(`结算单 ${order.orderNo} 创建成功! 是否前往"结算单管理"继续完善费用明细和上传材料?`, '提示', {
				confirmButtonText: '前往查看',
				cancelButtonText: '稍后',
				type: 'success'
			}).then(() => {
				this.$router.push({ path: '/spendingManage' });
			}).catch(() => { /* 用户选择"稍后", 留在当前页 */ });
		},

		isLiving(item) {
			// 明确标记为直播中
			if (item.meetingStatus === 'LIVING' || item.liveStatus === 1) return true;
			// 时间兜底：当前时间在会议时间范围内
			if (item.meetingStartTime) {
				const now = Date.now();
				const start = new Date(item.meetingStartTime).getTime();
				const end = item.meetingEndTime ? new Date(item.meetingEndTime).getTime() : (start + 7200000);
				if (now >= start && now <= end) return true;
			}
			return false;
		},

		isUpcoming(item) {
			// 明确标记为待开始
			if (['WAIT', 'WAIT_CLAIM'].includes(item.meetingStatus)) return true;
			// 时间兜底：会议开始时间在未来，且没有被标记为进行中
			if (item.meetingStartTime && !this.isLiving(item)) {
				const now = Date.now();
				const start = new Date(item.meetingStartTime).getTime();
				if (start > now) return true;
			}
			return false;
		},

		getStatusText(item) {
			if (this.isLiving(item)) return '进行中';
			if (this.isUpcoming(item)) return '未开始';
			return '已结束';
		},

		getStatusClass(item) {
			if (this.isLiving(item)) return 'status-living';
			if (this.isUpcoming(item)) return 'status-upcoming';
			return 'status-finished';
		},

		formatMonth(time) {
			if (!time) return '';
			return this.$moment(time).format('YYYY年MM月');
		},

		formatDay(time) {
			if (!time) return '';
			return this.$moment(time).format('DD');
		},

		formatTime(time) {
			if (!time) return '';
			return this.$moment(time).format('HH:mm');
		},

		/** 进入会议 - 先查询会议详情，判断是否需要入场验证 */
		async enterMeeting(item) {
			try {
				const res = await getMeetingDetail(item.id);
				if (res && res.code === 200) {
					const meeting = res.data?.meeting;

					// 如果开启了表单收集，跳转到入场验证页
					if (meeting && meeting.formCollectEnabled) {
						this.$router.push({
							name: 'meetingEntry',
							query: {
								meetingId: item.id,
								title: item.meetingTitle
							}
						});
						return;
					}
				}
			} catch (err) {
				console.warn('查询会议详情失败，直接进入会议室:', err);
			}

			// 未开启表单收集 或 查询失败 → 直接进入会议室
			this.$router.push({
				name: 'meetingRoom',
				query: { meetingId: item.id }
			});
		},

		goSign(item) {
			this.$router.push({ name: 'agreement', query: { mId: item.id } });
		},

		/** 跳转到会议详情页 */
		goMeetDetail(item) {
			this.$router.push({
				name: 'meetdetails',
				query: {
					mId: item.id,
					status: item.meetingStatus || 'SUCCESS',
					now: '3',
					from: 'myMeetings',   // 标记入口, 用于侧边栏高亮和返回跳转
					fromPage: this.page   // 当前列表页码, 返回时恢复
				}
			});
		},
	}
}
</script>

<style lang="scss" scoped>
#myMeetings {
	padding: 32px 40px;
	min-height: 100vh;
	background: #f0f2f8;

	/* .page-header / .filter-bar 已统一到 PageHeader 组件 */

	.meeting-list {
		min-height: 200px;
	}

	/* 分页已统一到 .app-pagination 全局类 */

	.meeting-card {
		display: flex;
		align-items: center;
		height: 64px;
		padding: 0 20px;
		margin-bottom: 6px;
		background: #fff;
		border-radius: 10px;
		border: 1px solid #eef0f6;
		transition: all 0.2s ease;
		cursor: default;

		&:hover {
			box-shadow: 0 4px 12px rgba(26, 26, 46, 0.05);
			border-color: #d8dce8;
		}

		&.is-completed {
			opacity: 0.72;

			&:hover { opacity: 0.9; }

			.time-section .day { color: #b0b7c8; }
		}

		.time-section {
			width: 64px;
			flex-shrink: 0;
			text-align: center;

			.month {
				font-size: 10px;
				color: #8f9bb3;
				font-weight: 500;
				letter-spacing: 0.3px;
			}

			.day {
				font-size: 20px;
				font-weight: 700;
				color: #1a1a2e;
				line-height: 1.1;
				margin: 1px 0;
			}

			.time {
				font-size: 11px;
				color: #8f9bb3;
				font-weight: 600;
			}
		}

		.divider {
			width: 1px;
			height: 36px;
			background: linear-gradient(180deg, transparent, #e4e7f0, transparent);
			margin: 0 16px;
			flex-shrink: 0;
		}

		.info-section {
			flex: 1;
			min-width: 0;

			.meeting-title {
				font-size: 14px;
				font-weight: 600;
				color: #1a1a2e;
				margin: 0 0 4px;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.meeting-meta {
				display: flex;
				gap: 14px;
				flex-wrap: nowrap;
				align-items: center;
				overflow: hidden;

				span {
					font-size: 11px;
					color: #8f9bb3;
					display: inline-flex;
					align-items: center;
					gap: 3px;
					white-space: nowrap;

					i {
						font-size: 12px;
						color: #b0b7c8;
					}
				}

				.mode-tag {
					padding: 2px 10px;
					border-radius: 10px;
					font-size: 11px;
					font-weight: 600;
					display: inline-flex;
					align-items: center;
					gap: 4px;

					i { font-size: 12px; color: inherit; }

					&.mode-online {
						background: #eef2ff;
						color: #667eea;
					}

					&.mode-offline {
						background: #fff8ed;
						color: #f5a623;
					}

					&.mode-other {
						background: #f4f5f7;
						color: #8f9bb3;
					}

					&.mode-default {
						background: #f4f5f7;
						color: #b0b7c8;
					}
				}
			}
		}

		.action-section {
			display: flex;
			align-items: center;
			gap: 8px;
			flex-shrink: 0;
			margin-left: 12px;

			.status-tag {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				gap: 4px;
				padding: 2px 10px;
				border-radius: 12px;
				font-size: 11px;
				font-weight: 600;
				white-space: nowrap;
				min-width: 54px;

				.status-dot {
					width: 6px;
					height: 6px;
					border-radius: 50%;
					background: currentColor;
					animation: pulse 1.5s infinite;
				}

				&.status-living {
					background: #e8f8f0;
					color: #00b574;
				}

				&.status-upcoming {
					background: #fff3e0;
					color: #f5a623;
				}

				&.status-finished {
					background: #f0f1f5;
					color: #8f9bb3;
				}
			}

			.live-badge {
				display: inline-flex;
				align-items: center;
				gap: 5px;
				padding: 4px 12px;
				border-radius: 20px;
				font-size: 11px;
				font-weight: 600;
				background: #fff0f0;
				color: #ff4d4f;
				white-space: nowrap;

				.live-dot-anim {
					width: 6px;
					height: 6px;
					border-radius: 50%;
					background: #ff4d4f;
					animation: livePulse 1.2s infinite;
				}
			}

			.btn-group {
				display: flex;
				gap: 8px;
			}

			.btn {
				display: inline-flex;
				align-items: center;
				gap: 4px;
				padding: 5px 12px;
				border: none;
				border-radius: 6px;
				font-size: 12px;
				font-weight: 600;
				cursor: pointer;
				transition: all 0.2s ease;
				white-space: nowrap;
				line-height: 1.4;

				i { font-size: 13px; }

				&.btn-sign {
					background: linear-gradient(135deg, #667eea, #764ba2);
					color: #fff;
					box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

					&:hover {
						transform: translateY(-1px);
						box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
					}
				}

				&.btn-signed {
					background: #e8f8f0;
					color: #00b574;
					cursor: default;
				}

				&.btn-meeting {
					background: #eef2ff;
					color: #667eea;

					&:hover {
						background: #dde4ff;
					}
				}

				&.btn-settle {
					background: linear-gradient(135deg, #67c23a, #85ce61);
					color: #fff;
					box-shadow: 0 4px 12px rgba(103, 194, 58, 0.25);

					&:hover {
						transform: translateY(-1px);
						box-shadow: 0 6px 16px rgba(103, 194, 58, 0.35);
					}
				}

				&.btn-settle-progress {
					border: 1px solid transparent;

					&.progress-collecting {
						background: #fff7e6;
						color: #d48806;
						border-color: #ffd591;
					}
					&.progress-auditing {
						background: #e6f7ff;
						color: #096dd9;
						border-color: #91d5ff;
					}
					&.progress-done {
						background: #f6ffed;
						color: #389e0d;
						border-color: #b7eb8f;
					}
					&.progress-rejected {
						background: #fff1f0;
						color: #cf1322;
						border-color: #ffa39e;
					}

					&:hover {
						transform: translateY(-1px);
						opacity: 0.9;
					}
				}

				&.btn-detail {
					background: linear-gradient(135deg, #409eff, #36b3ff);
					color: #fff;
					box-shadow: 0 4px 12px rgba(64, 158, 255, 0.25);

					&:hover {
						transform: translateY(-1px);
						box-shadow: 0 6px 16px rgba(64, 158, 255, 0.35);
					}
				}
			}
		}
	}

	.empty-state {
		text-align: center;
		padding: 80px 0;

		.empty-icon {
			width: 80px;
			height: 80px;
			border-radius: 50%;
			background: #eef0f6;
			display: flex;
			align-items: center;
			justify-content: center;
			margin: 0 auto 16px;

			i {
				font-size: 36px;
				color: #b0b7c8;
			}
		}

		p {
			color: #8f9bb3;
			font-size: 15px;
			margin: 0 0 4px;
			font-weight: 500;
		}

		.empty-hint {
			font-size: 13px;
			color: #c0c7d6;
			font-weight: 400;
		}
	}

	@keyframes pulse {
		0%, 100% { opacity: 1; }
		50% { opacity: 0.3; }
	}

	@keyframes livePulse {
		0%, 100% { opacity: 1; transform: scale(1); }
		50% { opacity: 0.4; transform: scale(0.8); }
	}
}
</style>
